import type { Icon } from '@onefootprint/icons';
import * as RadixDialog from '@radix-ui/react-dialog';
import { cx } from 'class-variance-authority';
import type React from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import CaptureEscKeydown, { getHandler, type OnEscKeydownHandler } from '../capture-esc-keydown';
import Overlay from '../overlay';
import Container from './container';
import type { DialogButton, DialogLinkButton, DialogSize } from './dialog.types';
import Footer from './footer';
import DialogHeader from './header';

export type DialogProps = {
  children?: React.ReactNode;
  container?: HTMLElement | null;
  contentClassName?: string;
  isConfirmation?: boolean;
  isCentered?: boolean;
  linkButton?: DialogLinkButton;
  noPadding?: boolean;
  noScroll?: boolean;
  onClose: OnEscKeydownHandler;
  /** Runs before focus returns to what opened the dialog; `preventDefault()` keeps focus where the dialog's action put it. */
  onCloseAutoFocus?: (event: Event) => void;
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  open: boolean;
  preventEscapeKeyDown?: boolean;
  primaryButton?: DialogButton;
  secondaryButton?: DialogButton;
  size?: DialogSize;
  title: string;
  width?: React.CSSProperties['width'];
  headerIcon?: {
    component: Icon;
    ariaLabel: string;
    onClick: () => void;
  };
  headerAction?: {
    component: Icon;
    ariaLabel: string;
    onClick: () => void;
  };
  headerSecondaryAction?: {
    component: Icon;
    ariaLabel: string;
    onClick: () => void;
  };
};

const Dialog = forwardRef<HTMLDivElement, DialogProps>(
  (
    {
      children,
      container,
      contentClassName,
      headerAction,
      headerSecondaryAction,
      headerIcon,
      isConfirmation = false,
      isCentered: isCenteredProp,
      linkButton,
      noPadding = false,
      noScroll = false,
      onClose,
      onCloseAutoFocus,
      onEscapeKeyDown,
      open,
      preventEscapeKeyDown,
      primaryButton,
      secondaryButton,
      size = 'default',
      title,
      width,
    },
    ref,
  ) => {
    const hasFooter = primaryButton || secondaryButton || linkButton;
    const contentRef = useRef<HTMLDivElement>(null);
    const [hasScroll, setHasScroll] = useState(false);
    const isCentered = isCenteredProp ?? isConfirmation;

    useEffect(() => {
      const checkScroll = () => {
        if (contentRef.current) {
          const { scrollHeight, clientHeight, scrollTop } = contentRef.current;
          const hasMoreContent = scrollHeight > clientHeight;
          const reachedBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight - 1;

          setHasScroll(hasMoreContent && !reachedBottom);
        }
      };

      const contentElement = contentRef.current;
      if (contentElement) {
        checkScroll();

        contentElement.addEventListener('scroll', checkScroll);
        window.addEventListener('resize', checkScroll);

        const observer = new ResizeObserver(checkScroll);
        observer.observe(contentElement);

        return () => {
          contentElement.removeEventListener('scroll', checkScroll);
          window.removeEventListener('resize', checkScroll);
          observer.disconnect();
        };
      }
    }, []);

    const handleClose = getHandler(onClose);

    return (
      <RadixDialog.Root open={open} onOpenChange={handleClose} modal>
        <RadixDialog.Portal container={container}>
          <RadixDialog.Overlay asChild>
            <Overlay isVisible={open} isConfirmation={isConfirmation} />
          </RadixDialog.Overlay>
          <Container
            aria-label={title}
            aria-describedby={undefined}
            isConfirmation={isConfirmation}
            isCentered={isCentered}
            onClose={handleClose}
            onCloseAutoFocus={onCloseAutoFocus}
            onEscapeKeyDown={onEscapeKeyDown}
            preventEscapeKeyDown={preventEscapeKeyDown}
            ref={ref}
            size={size}
            style={{ width }}
            open={open}
          >
            <DialogHeader
              title={title}
              onClose={handleClose}
              icon={headerIcon}
              action={headerAction}
              secondaryAction={headerSecondaryAction}
            />
            <div
              ref={contentRef}
              className={cx(
                'flex-1 overflow-auto min-h-0',
                {
                  'p-0': noPadding,
                  'p-6': !noPadding,
                  'overflow-hidden': noScroll,
                },
                contentClassName,
              )}
            >
              <CaptureEscKeydown id={title} onEscKeydown={onClose} />

              {children}
            </div>
            {hasFooter && (
              <Footer
                hasScroll={hasScroll}
                linkButton={linkButton}
                primaryButton={primaryButton}
                secondaryButton={secondaryButton}
                size={size}
              />
            )}
          </Container>
        </RadixDialog.Portal>
      </RadixDialog.Root>
    );
  },
);

export default Dialog;
